/*
 * FILE: assets/vertex-custom-ui.js
 * PURPOSE: Intercepts Discovery AI events and renders
 *          Vogue Rugs branded UI instead of default orange UI.
 *
 * REQUIRES: Discovery AI to have installed:
 *   - assets/vertex-search.js
 *   - snippets/theme-orchestrator.liquid
 *
 * HOW IT WORKS:
 *   1. Discovery AI fetches and processes product data
 *   2. Before rendering their default UI, they fire a CustomEvent
 *   3. We call event.preventDefault() to cancel their default render
 *   4. We render our own branded cards using their data payload
 */

/* ─── HELPERS ─── */
function buildHandle(p) {
  return (p.handle || p.shopify_handle || (p.id || '').split('/').pop())
    .replace('owfam-', '');
}

function buildPrice(p) {
  if (p.has_explicit_size_intent || p.min_price === p.max_price) {
    return '$' + Number(p.price || p.min_price || 0).toLocaleString();
  }
  return '$' + Number(p.min_price || 0).toLocaleString()
    + ' – $' + Number(p.max_price || 0).toLocaleString();
}

function safeTitle(p) {
  return (p.title || 'Untitled').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function buildMetaLine(p) {
  var brand = Array.isArray(p.brands) ? p.brands[0] : (p.brands || p.brand);
  return [brand, p.selected_size || p.size_bucket, p.shape]
    .filter(Boolean)
    .join(' · ');
}

// Deliver WebP for the grid. Discovery AI GCS images store the webp at
// <product>/card/<file>.webp — the SAME base filename as the .jpg — so we build that
// URL directly (no proxy). e.g. .../als-8033k-red/atlas_8033k.jpg
//                            ->  .../als-8033k-red/card/atlas_8033k.webp
function vrToWebp(url) {
  if (!url) return url;
  if (!window.CloudImageHandler || !window.CloudImageHandler.isCloudImage(url)) return url;
  try {
    const qIndex = url.indexOf('?');
    const query = qIndex === -1 ? '' : url.slice(qIndex);
    let parts = (qIndex === -1 ? url : url.slice(0, qIndex)).split('/');
    parts[parts.length - 1] = parts[parts.length - 1].replace(/\.(jpe?g|png|gif)$/i, '.webp');
    const prev = parts[parts.length - 2];
    if (prev === 'thumb' || prev === 'full') {
      parts[parts.length - 2] = 'card';
    } else if (prev !== 'card') {
      parts.splice(parts.length - 1, 0, 'card');
    }
    return parts.join('/') + query;
  } catch (e) { return url; }
}

function buildImageTag(p, handle, cssClass) {
  // Prefer the API's already-webp fields, then fall back to other sources.
  const rawSrc = p.primary_image_url
    || (p.images && p.images[0] && (p.images[0].uri || p.images[0].url))
    || p.gcs_image_url || p.shopify_image_url || p.image_url || p.image || '';
  const src = vrToWebp(rawSrc);
  const fallbackImages = [
    { uri: p.primary_image_url }, { uri: p.gcs_image_url }, { uri: p.image_url }, { uri: p.visualizer_image_url }, { uri: p.shopify_image_url }, { uri: p.image }, { uri: rawSrc }
  ].filter(img => img.uri).concat(p.images && p.images.length ? p.images : []);

  let imgages = '';
  try {
    imgages = btoa(unescape(encodeURIComponent(JSON.stringify(fallbackImages))));
  } catch (_) {
    imgages = '';
  }

  const attrTitle = (p.title || 'Untitled').replace(/"/g, '&quot;');
  const attrHandle = (handle || '').replace(/"/g, '&quot;');
  return `<img
    src="${src}"
    alt="${attrTitle}"
    class="${cssClass}"
    loading="lazy"
    data-current-url="${rawSrc}"
    data-vr-images="${imgages}"
    data-vr-handle="${attrHandle}"
    data-vr-title="${attrTitle}"
    onerror="if(window.CloudImageHandler) window.CloudImageHandler.onError(this)"
  />`;
}

function buildSeeInRoomBtn(p, handle) {
  const rawRug = p.visualizer_image_url || p.shopify_image_url || p.gcs_image_url || p.image_url || p.image || '';
  const cleanRug = typeof normalizeRugUrl === 'function' ? normalizeRugUrl(rawRug) : rawRug;
  return `<button
    class="see-in-room-overlay-btn"
    style="position: absolute; bottom: 12px; left: 12px; background: rgba(255,255,255,0.9); border: 1px solid transparent; color: #1a1a1a; padding: 6px 12px; font-size: 11px; font-weight: 600; text-align: center; pointer-events: auto; cursor: pointer; display: flex; align-items: center; justify-content: center; border-radius: 20px; transition: all 0.2s ease; box-shadow: 0 2px 4px rgba(0,0,0,0.1); z-index: 10;"
    onmouseover="this.style.borderColor='#c9a042'; this.style.color='#c9a042'; this.style.background='#ffffff';"
    onmouseout="this.style.borderColor='transparent'; this.style.color='#1a1a1a'; this.style.background='rgba(255,255,255,0.9)';"
    data-rug="${cleanRug}"
    data-product-url="/products/${handle}"
    data-shape="${p.shape || ''}"
    data-size="${p.selected_size || p.size_bucket || ''}">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> See in My Room
  </button>`;
}

function buildSwatches(p, pdpUrl) {
  var variants = p.variants_info || [];
  var isExplicitSize = !!p.has_explicit_size_intent;

  if (isExplicitSize || variants.length === 0) {
    return '';
  }

  var isMobile = window.innerWidth < 750;
  var initialLimit = (isMobile && variants.length > 2) ? 0 : 5;
  var hasMore = variants.length > initialLimit;

  var swatchesHtml = '<div class="vr-size-swatches">';

  for (var i = 0; i < variants.length; i++) {
    var v = variants[i];
    var style = i >= initialLimit ? 'display:none;' : '';
    var className = i >= initialLimit ? 'vr-swatch-hidden' : '';
    var vUrl = v.url || pdpUrl;

    if (vUrl && vUrl.indexOf("/products/") > -1) {
      var parts = vUrl.split("/products/");
      vUrl = "/products/" + parts[parts.length - 1];
    }

    if (v.variant_id) {
      try {
        var urlObj = new URL(vUrl, window.location.origin);
        urlObj.searchParams.set("variant", v.variant_id);
        vUrl = urlObj.pathname + urlObj.search;
      } catch (e) {
        if (vUrl.indexOf("variant=") === -1) {
          vUrl += (vUrl.indexOf("?") > -1 ? "&" : "?") + "variant=" + v.variant_id;
        }
      }
    }

    swatchesHtml += '<a href="' + vUrl + '" class="' + className + '" onclick="event.stopPropagation();" style="' + style + '">' + (v.size || '') + '</a>';
  }

  if (hasMore) {
    var moreHtml = initialLimit === 0
      ? '<span style="border-bottom:1px solid #666; padding-bottom:1px;">' + variants.length + ' Sizes Available</span>'
      : '+' + (variants.length - initialLimit) + ' more';

    swatchesHtml += '<span class="vr-swatch-more" onclick="event.preventDefault(); event.stopPropagation(); var siblings = this.parentElement.querySelectorAll(\'.vr-swatch-hidden\'); for(var j=0; j<siblings.length; j++){siblings[j].style.display=\'inline-block\';} this.style.display=\'none\';">' + moreHtml + '</span>';
  }

  swatchesHtml += '</div>';
  return swatchesHtml;
}

/* =========================================================
   Discovery results focus
   Mobile-first behavior:
   - keep discovery context visible
   - show first results immediately below
   - account for sticky Shopify header
   ========================================================= */

function focusDiscoveryResults(container, preferredWidget) {
  if (!container) return;

  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      var widget =
        preferredWidget ||
        document.getElementById('rug-hero');

      var header =
        document.querySelector('.shopify-section-header-sticky') ||
        document.querySelector('#shopify-section-header') ||
        document.querySelector('header');

      var headerHeight = header
        ? Math.min(header.getBoundingClientRect().height, 140)
        : 0;

      var viewportHeight =
        window.innerHeight ||
        document.documentElement.clientHeight ||
        800;

      var isMobile = window.matchMedia('(max-width: 600px)').matches;

      var resultsTop =
        container.getBoundingClientRect().top +
        window.pageYOffset;

      var top;

      if (isMobile) {
        /*
         * Keep some discovery context visible above the results.
         * The first products should then appear directly underneath.
         */
        var contextHeight = Math.min(
          viewportHeight * 0.42,
          260
        );

        top =
          resultsTop -
          contextHeight -
          headerHeight;
      } else if (widget) {
        /*
         * Desktop:
         * Keep the full discovery widget visible with results below.
         */
        top =
          widget.getBoundingClientRect().top +
          window.pageYOffset -
          headerHeight -
          12;
      } else {
        top =
          resultsTop -
          headerHeight -
          20;
      }

      window.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth'
      });
    });
  });
}

/* ─── SEARCH RESULTS GRID ─── */
/* Renders on /search page inside #vr-search-results container */
document.addEventListener('vertex:search:results', function (event) {
  var container = document.getElementById('vr-search-results');
  if (!container) return;

  event.preventDefault();

  var items = event.detail.results || [];

  if (!items.length) {
    container.innerHTML = '<p class="vr-search-empty">No rugs found matching your search.</p>';
    return;
  }

  var html = items.map(function (raw) {
    var p = raw.product && raw.product.product
      ? raw.product.product
      : (raw.product || raw);

    var handle = buildHandle(p);
    var pdpUrl = '/products/' + handle;
    var price = buildPrice(p);
    var meta = buildMetaLine(p);

    var brandNameHtml = '';
    if (p.brands || p.brand) {
      var b = Array.isArray(p.brands) ? p.brands[0] : (p.brands || p.brand);
      if (b) brandNameHtml = '<div class="rug-vendor" style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 600; color: #555; margin-bottom: 4px;">' + b + '</div>';
    }

    var visualizerUrl = p.gcs_image_url || p.visualizer_image_url || p.shopify_image_url || p.image_url || p.image || '';

    return '<div class="vertex-grid-item" style="display: flex; flex-direction: column;">'
      + '<div class="vertex-card-wrapper" style="width: 100%;">'
      + '<div class="vertex-card">'
      + '<div class="vertex-card__inner" style="position: relative; aspect-ratio: 4/5; background: #f5f5f5; border-radius: 6px; overflow: hidden; display: block; width: 100%;">'
      + '<favorites-button class="wishlist-btn wishlist-overlay" product-handle="' + handle + '" product-title="' + safeTitle(p) + '" product-price="' + price + '" product-image="' + (p.image_url || p.primary_image_url || p.gcs_image_url || '') + '" product-url="' + pdpUrl + '" style="position: absolute; top: 8px; right: 8px; z-index: 2; color: #111; background: rgba(255,255,255,0.9); border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 5px rgba(0,0,0,0.15);"></favorites-button>'
      + '<span class="fs-ribbon" aria-label="Free shipping">Free&nbsp;Shipping</span>'
      + '<a href="' + pdpUrl + '" class="vertex-unstyled-link" style="display: block; width: 100%; height: 100%;">'
      + buildImageTag(p, handle, 'vr-prod-img')
      + '</a>'
      + (visualizerUrl ? '<button type="button" class="see-in-room-overlay-btn visualizer-trigger" style="position: absolute; bottom: 12px; left: 12px; background: rgba(255,255,255,0.9); border: 1px solid transparent; color: #1a1a1a; padding: 6px 12px; font-size: 11px; font-weight: 600; text-align: center; pointer-events: auto; cursor: pointer; display: flex; align-items: center; justify-content: center; border-radius: 20px; transition: all 0.2s ease; box-shadow: 0 2px 4px rgba(0,0,0,0.1); z-index: 10;" onmouseover="this.style.borderColor=\'#c9a042\'; this.style.color=\'#c9a042\'; this.style.background=\'#ffffff\';" onmouseout="this.style.borderColor=\'transparent\'; this.style.color=\'#1a1a1a\'; this.style.background=\'rgba(255,255,255,0.9)\';" data-rug="' + visualizerUrl + '" data-product-url="' + pdpUrl + '" data-product-handle="' + handle + '" data-shape="' + (p.shape || '') + '" data-size="' + (p.selected_size || p.size_bucket || '') + '" data-variants="' + (function (v) { try { return btoa(unescape(encodeURIComponent(JSON.stringify(v || [])))); } catch (_) { return ''; } })(p.variants_info) + '"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> See in My Room</button>' : '')
      + '</div>'
      + '<div class="vertex-card__content" style="padding-top: 8px; display: block !important;">'
      + brandNameHtml
      + '<h3 class="vertex-card-title" style="margin: 0 0 4px 0; font-weight: 900; text-transform: uppercase; letter-spacing: 0.05em; font-size: 12px; line-height: 1.3; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">'
      + '<a href="' + pdpUrl + '" class="vertex-unstyled-link" style="text-decoration:none; color: #000;">' + (p.title || '') + '</a>'
      + '</h3>'
      + (meta ? '<div class="card-information vertex-meta-line" style="font-size:10px; color:#aaa; margin:4px 0; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; line-height: 1.2;">' + meta + '</div>' : '')
      + '<div class="vertex-card-info" style="margin-top: 4px;">'
      + '<p class="vr-prod-price" style="margin: 0; font-weight: 900; color: #000; font-size: 15px; display: block;">' + price + '</p>'
      + buildSwatches(p, pdpUrl)
      + '</div>'
      + '</div></div></div></div>';
  }).join('');

  container.innerHTML = html;

  focusDiscoveryResults(
    container,
    document.getElementById('rug-hero')
  );
});

/* ─── STYLE AI CAROUSEL ─── */
/* Renders inside #vr-styleai-results on homepage */
/* ─── PDP RECOMMENDATIONS CAROUSEL ─── */
/* Renders inside #vr-pdp-results on product pages */
document.addEventListener('vertex:recommendations:results', function (event) {
  var activeTenant = window.shopkeeperTenant || window.shopkeeper_tenant;
  if (activeTenant && activeTenant !== 'vogue_rugs' && activeTenant !== 'vogue') {
    return;
  }

  var source = event.detail.source;
  var items = event.detail.results || [];
  var containerId = event.detail.containerId;

  /* Pick target container based on source */
  var targetId = source === 'pdp'
    ? (containerId || 'vr-pdp-results')
    : 'vr-styleai-results';

  var container = document.getElementById(targetId);
  if (!container) return;

  event.preventDefault();

  if (!items.length) return;

  /* Normalize — Style AI items can be nested */
  var normalized = items.map(function (raw) {
    return raw.product && raw.product.product
      ? raw.product.product
      : (raw.product || raw);
  });

  var html = normalized.map(function (p) {
    var handle = buildHandle(p);
    var pdpUrl = '/products/' + handle;
    var price = buildPrice(p);
    var meta = buildMetaLine(p);

    var brandNameHtml = '';
    if (p.brands || p.brand) {
      var b = Array.isArray(p.brands) ? p.brands[0] : (p.brands || p.brand);
      if (b) brandNameHtml = '<div class="rug-vendor" style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 600; color: #555; margin-bottom: 4px;">' + b + '</div>';
    }

    var visualizerUrl = p.gcs_image_url || p.visualizer_image_url || p.shopify_image_url || p.image_url || p.image || '';

    return '<div class="vertex-grid-item" style="display: flex; flex-direction: column;">'
      + '<div class="vertex-card-wrapper" style="width: 100%;">'
      + '<div class="vertex-card">'
      + '<div class="vertex-card__inner" style="position: relative; aspect-ratio: 4/5; background: #f5f5f5; border-radius: 6px; overflow: hidden; display: block; width: 100%;">'
      + '<favorites-button class="wishlist-btn wishlist-overlay" product-handle="' + handle + '" product-title="' + safeTitle(p) + '" product-price="' + price + '" product-image="' + (p.image_url || p.primary_image_url || p.gcs_image_url || '') + '" product-url="' + pdpUrl + '" style="position: absolute; top: 8px; right: 8px; z-index: 2; color: #111; background: rgba(255,255,255,0.9); border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 5px rgba(0,0,0,0.15);"></favorites-button>'
      + '<span class="fs-ribbon" aria-label="Free shipping">Free&nbsp;Shipping</span>'
      + '<a href="' + pdpUrl + '" class="vertex-unstyled-link" style="display: block; width: 100%; height: 100%;">'
      + buildImageTag(p, handle, 'vr-prod-img')
      + '</a>'
      + (visualizerUrl ? '<button type="button" class="see-in-room-overlay-btn visualizer-trigger" style="position: absolute; bottom: 12px; left: 12px; background: rgba(255,255,255,0.9); border: 1px solid transparent; color: #1a1a1a; padding: 6px 12px; font-size: 11px; font-weight: 600; text-align: center; pointer-events: auto; cursor: pointer; display: flex; align-items: center; justify-content: center; border-radius: 20px; transition: all 0.2s ease; box-shadow: 0 2px 4px rgba(0,0,0,0.1); z-index: 10;" onmouseover="this.style.borderColor=\'#c9a042\'; this.style.color=\'#c9a042\'; this.style.background=\'#ffffff\';" onmouseout="this.style.borderColor=\'transparent\'; this.style.color=\'#1a1a1a\'; this.style.background=\'rgba(255,255,255,0.9)\';" data-rug="' + visualizerUrl + '" data-product-url="' + pdpUrl + '" data-product-handle="' + handle + '" data-shape="' + (p.shape || '') + '" data-size="' + (p.selected_size || p.size_bucket || '') + '" data-variants="' + (function (v) { try { return btoa(unescape(encodeURIComponent(JSON.stringify(v || [])))); } catch (_) { return ''; } })(p.variants_info) + '"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> See in My Room</button>' : '')
      + '</div>'
      + '<div class="vertex-card__content" style="padding-top: 8px; display: block !important;">'
      + brandNameHtml
      + '<h3 class="vertex-card-title" style="margin: 0 0 4px 0; font-weight: 900; text-transform: uppercase; letter-spacing: 0.05em; font-size: 12px; line-height: 1.3; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">'
      + '<a href="' + pdpUrl + '" class="vertex-unstyled-link" style="text-decoration:none; color: #000;">' + (p.title || '') + '</a>'
      + '</h3>'
      + (meta ? '<div class="card-information vertex-meta-line" style="font-size:10px; color:#aaa; margin:4px 0; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; line-height: 1.2;">' + meta + '</div>' : '')
      + '<div class="vertex-card-info" style="margin-top: 4px;">'
      + '<p class="vr-prod-price" style="margin: 0; font-weight: 900; color: #000; font-size: 15px; display: block;">' + price + '</p>'
      + buildSwatches(p, pdpUrl)
      + '</div>'
      + '</div></div></div></div>';
  }).join('');

  if (container) {
    container.innerHTML = html;

    var widget = null;

    if (source !== 'pdp') {
      widget = document.getElementById('rug-hero');
    }

    if (!widget) {
      widget =
        container.closest('.shopify-section') ||
        container.closest('section');
    }

    focusDiscoveryResults(container, widget);
  }
});

/* ─── CARD STYLES ─── */
(function injectStyles() {
  var activeTenant = window.shopkeeperTenant || window.shopkeeper_tenant;
  if (activeTenant && activeTenant !== 'vogue_rugs' && activeTenant !== 'vogue') {
    return;
  }
  var style = document.createElement('style');
  style.textContent = `
    /* Search grid */
    #vr-search-results {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
      gap: 24px;
      padding: 20px 0;
    }
    .vr-prod-card {
      display: flex;
      flex-direction: column;
      border: 1px solid #e4e0d8;
      border-radius: 8px;
      overflow: hidden;
      transition: transform .2s, box-shadow .2s;
    }
    .vr-prod-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(0,0,0,.08);
    }
    .vr-prod-img-wrap {
      position: relative !important;
      width: 100% !important;
      height: 0 !important;
      padding-bottom: 125% !important; /* Forces 4:5 ratio identically everywhere */
      overflow: hidden !important;
      background: #f5f3ef !important;
      display: block !important;
    }
    .vr-prod-img {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
      mix-blend-mode: multiply !important;
      transition: transform .4s;
    }
    .vr-prod-card:hover .vr-prod-img { transform: scale(1.04); }
    .vr-prod-info { padding: 14px 16px; display: flex; flex-direction: column; gap: 5px; }
    .vr-prod-meta { font-size: 11px; color: #888; }
    .vr-prod-name { font-size: 13px; font-weight: 500; color: #1a1a1a; text-decoration: none; line-height: 1.35; }
    .vr-prod-name:hover { color: #c9a042; }
    .vr-prod-price { font-size: 14px; font-weight: 600; color: #1a1a1a; margin-top: 2px; }

    /* Carousel cards (Style AI + PDP) */
    #vr-styleai-results,
    #vr-pdp-results {
      display: flex;
      gap: 18px;
      overflow-x: auto;
      padding-bottom: 8px;
      scrollbar-width: none;
    }
    #vr-styleai-results::-webkit-scrollbar,
    #vr-pdp-results::-webkit-scrollbar { display: none; }
    .vr-car-card {
      flex: 0 0 190px;
      min-width: 190px;
      display: flex;
      flex-direction: column;
    }
    .vr-car-img-wrap {
      position: relative !important;
      width: 100% !important;
      height: 0 !important;
      padding-bottom: 125% !important;
      background: #f5f3ef !important;
      overflow: hidden !important;
      margin-bottom: 10px !important;
    }
    .vr-car-img {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
      mix-blend-mode: multiply !important;
      transition: transform .4s;
    }
    .vr-car-card:hover .vr-car-img { transform: scale(1.04); }
    .vr-car-info { display: flex; flex-direction: column; gap: 4px; }
    .vr-car-name {
      font-size: 11px; font-weight: 800; color: #000; text-decoration: none; text-transform: uppercase; letter-spacing: 0.05em; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; margin-bottom: 4px; line-height: 1.3;
    }
    .vr-car-name:hover { color: #c9a042; }
    .vr-car-price { font-size: 13px; font-weight: 600; color: #1a1a1a; }

    /* See in My Room button */
    .vr-sir-btn {
      display: block;
      width: 100%;
      margin-top: 8px;
      padding: 9px;
      background: transparent;
      border: 1.5px solid #c9a042;
      color: #c9a042;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: .1em;
      text-transform: uppercase;
      cursor: pointer;
      transition: background .2s, color .2s;
    }
    .vr-sir-btn:hover {
      background: #c9a042;
      color: #fff;
    }

    /* Swatches */
    .vr-size-swatches {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 8px;
    }
    .vr-size-swatches a {
      font-size: 10px;
      color: #888;
      border: 1px solid #e4e0d8;
      padding: 4px 8px;
      border-radius: 4px;
      text-transform: uppercase;
      font-weight: 500;
      text-decoration: none;
      background: #fff;
      transition: all 0.2s ease;
      display: inline-block;
    }
    .vr-size-swatches a:hover {
      background: #f5f3ef;
      color: #1a1a1a;
      border-color: #c9a042;
    }
    .vr-swatch-more {
      font-size: 10px;
      color: #888;
      padding: 4px 0;
      font-weight: 500;
      cursor: pointer;
      text-transform: uppercase;
      margin-left: 4px;
      transition: color 0.2s ease;
      display: inline-block;
    }
    .vr-swatch-more:hover {
      color: #c9a042;
    }

    /* Empty state */
    .vr-search-empty {
      font-size: 14px;
      color: #888;
      padding: 40px 0;
      text-align: center;
    }
  `;
  document.head.appendChild(style);
})();